import {Alert, Box, Button, Switch, Tooltip} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {useEffect, useRef, useState} from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import useNotification from '../../../../utility/hooks/useNotification';
import {useGetWelfareSettingsQuery, useManageWelfareOptionsMutation} from '../../../../api/services/lookupsService';
import PropTypes from 'prop-types';
import SectionHeading from './SectionHeading';
import DescriptionSettings from './DescriptionSettings';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';

const WelfareSettings = ({description, onDescriptionChange}) => {
    const {data: welfareOptions = {options: []}, refetch} = useGetWelfareSettingsQuery();
    const [manageWelfareOptions, {isSuccess, isError, error}] = useManageWelfareOptionsMutation();
    const {showSuccessNotification, showErrorNotification} = useNotification();
    const [rows, setRows] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        setRows((welfareOptions.options ?? []).map(option => ({
            id: option.id,
            title: option.title,
            shortDescription: option.shortDescription,
            comment: option.comment,
            hasComment: option.hasComment,
            isOurs: !option.schoolId,
            hidden: !!option.hidden
        })));
    }, [welfareOptions]);

    useEffect(() => {
        if (isSuccess) {
            showSuccessNotification('Welfare questions saved');
            refetch();
        }

        if (isError) {
            showErrorNotification(error?.data?.validationErrors?.[0]?.errorMessage ??
                'Failed to save the welfare questions');
        }
    }, [isSuccess, isError]);

    const move = (from, to) => {
        if (to < 0 || to >= rows.length) {
            return;
        }

        const next = [...rows];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setRows(next);
    };

    const textFields = [
        {
            label: 'Question a parent reads',
            name: 'title',
            required: true,
            multiline: true,
            rows: 3,
            initialValue: selectedRow?.title ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 1000,
                message: !value || !value.trim()
                    ? 'Please write the question as a parent will read it.'
                    : 'A question cannot exceed 1000 characters.'
            })
        },
        {
            label: 'Short name for filters and reports',
            name: 'shortDescription',
            required: true,
            initialValue: selectedRow?.shortDescription ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 100,
                message: !value || !value.trim()
                    ? 'Please give a short name, such as Young Carer.'
                    : 'A short name cannot exceed 100 characters.'
            })
        }
    ];

    // Two questions with the same wording or short name would show as one name in the filters.
    const clash = (data, except) => {
        const key = value => (value ?? '').trim().toLowerCase();
        const others = rows.filter(row => row !== except);

        if (key(data.title) && others.some(row => key(row.title) === key(data.title))) {
            return `"${data.title.trim()}" is already on the list.`;
        }

        if (key(data.shortDescription) && others.some(row => key(row.shortDescription) === key(data.shortDescription))) {
            return `The short name "${data.shortDescription.trim()}" is already used.`;
        }

        return null;
    };

    const handleAdd = data => {
        const problem = clash(data);

        if (problem) {
            showErrorNotification(problem);
            return;
        }

        setRows([...rows, {
            id: null,
            title: data.title,
            shortDescription: data.shortDescription,
            comment: null,
            hasComment: false,
            isOurs: false,
            hidden: false
        }]);
        setOpenAddDialog(false);
    };

    const handleEdit = data => {
        const problem = clash(data, selectedRow);

        if (problem) {
            showErrorNotification(problem);
            return;
        }

        setRows(rows.map(row => row === selectedRow
            ? {...row, title: data.title, shortDescription: data.shortDescription}
            : row));
        setOpenEditDialog(false);
        setSelectedRow(null);
    };

    const handleSave = async () => {
        await manageWelfareOptions({
            options: rows.map(row => ({
                id: row.id,
                shortDescription: row.shortDescription ?? null,
                description: row.title,
                comment: row.comment ?? null,
                hasComment: !!row.hasComment,
                hidden: !!row.hidden
            }))
        });
    };

    return (
        <Box>
            <SectionHeading
                title="Welfare"
                testId="welfare-heading"
                help="The questions a parent answers in the Welfare section of a child's profile. Turn off any of ours
                the school does not use, and add questions of the school's own. Turning one off leaves it on the
                children who already have it ticked."
            />
            <DescriptionSettings
                description={description}
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.welfare]}
                onDescriptionChange={onDescriptionChange}
            />
            <Box marginTop={2} marginBottom={2}>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: {xs: '100%', sm: 'auto'},
                        '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}
                    }}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    onClick={() => {
                        setSelectedRow(null);
                        setOpenAddDialog(true);
                    }}
                >
                    Add new
                </Button>
            </Box>
            <List disablePadding sx={{width: '100%'}}>
                {rows.map((row, index) => (
                    <ListItem
                        key={`welfare-${row.id ?? 'new'}-${index}`}
                        sx={{
                            px: 1,
                            py: 0.25,
                            backgroundColor: index % 2 === 0 ? '#F0FBFA' : '#FFFFFF',
                            '&:hover': {backgroundColor: '#D4F4F0'}
                        }}
                    >
                        <ListItemText
                            sx={{my: 0}}
                            primary={
                                <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{opacity: row.hidden ? 0.5 : 1}}>
                                        {row.title}
                                    </Typography>
                                    <Box display="flex" alignItems="center">
                                        {row.isOurs
                                            ? <Tooltip title={row.hidden ? 'Turned off' : 'In use'}>
                                                <Switch
                                                    size="small"
                                                    checked={!row.hidden}
                                                    onChange={event => setRows(rows.map(r => r === row
                                                        ? {...r, hidden: !event.target.checked}
                                                        : r))}
                                                    inputProps={{'aria-label': `Use ${row.title}`}}
                                                />
                                            </Tooltip>
                                            : null}
                                        <IconButton
                                            size="small"
                                            disabled={index === 0}
                                            onClick={() => move(index, index - 1)}
                                            aria-label="Move question up"
                                        >
                                            <ArrowUpwardOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={index === rows.length - 1}
                                            onClick={() => move(index, index + 1)}
                                            aria-label="Move question down"
                                        >
                                            <ArrowDownwardOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={row.isOurs}
                                            onClick={() => {
                                                setSelectedRow(row);
                                                setOpenEditDialog(true);
                                            }}
                                            aria-label="Edit question"
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={row.isOurs}
                                            onClick={() => setRows(rows.filter(r => r !== row))}
                                            aria-label="Remove question"
                                        >
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </Stack>
                            }
                        />
                    </ListItem>
                ))}
            </List>
            <Box
                display={'flex'}
                alignItems={'center'}
                gap={2}
                py={1}
                flexDirection={{xs: 'column', sm: 'row'}}
            >
                <Alert
                    severity="info"
                    sx={{
                        flexGrow: 1,
                        py: 0,
                        width: {xs: '100%', sm: 'auto'},
                        '& .MuiAlert-message': {fontSize: '0.75rem', py: 0.75}
                    }}
                >
                    Our questions cannot be reworded, so that every school records safeguarding the same way.
                </Alert>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: {xs: '100%', sm: 'auto'},
                        '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}
                    }}
                    variant="contained"
                    onClick={handleSave}
                >
                    Save
                </Button>
            </Box>
            <AdmicityDialog
                handleClose={() => {
                    setOpenAddDialog(false);
                    setOpenEditDialog(false);
                    setSelectedRow(null);
                }}
                title={openAddDialog ? 'New Welfare Question' : 'Update Welfare Question'}
                open={openAddDialog || openEditDialog}
                actions={[]}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={data => openAddDialog ? handleAdd(data) : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
};

WelfareSettings.propTypes = {
    description: PropTypes.string,
    onDescriptionChange: PropTypes.func
};

export default WelfareSettings;
